import { ENVIRONMENT_COLORS, type Environment, NAME_MAX, slugify } from '@fleequid-secrets/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { api, type EnvironmentInput, messageOf } from '@/api/client';
import { Icon } from '@/components/Icon';
import { plural } from '@/lib/format';
import { useOverlay } from '@/lib/overlay';
import { Link } from '@/lib/router';
import { vaultPath } from '@/lib/routes';
import { Cancelled, guarded, useShell } from '@/lib/shell';

// ENVIRONMENTS (admins): their names, addresses, colours, order, and which are protected. Each has its own data key,
// made with it; deleting one deletes everything it holds, its address typed to confirm.
export function Environments(): ReactNode {
    const shell = useShell();
    const { environments, say } = shell;
    const queryClient = useQueryClient();
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<Environment | null>(null);
    const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['environments'] });
    const move = (index: number, step: number): void => {
        const ids = environments.map((environment) => environment.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + step, 0, moved as string);
        api.reorderEnvironments(ids)
            .then(refresh)
            .catch((error: unknown) => say(messageOf(error)));
    };

    return (
        <main className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Environments</h1>
                    <p>
                        Where the variables live, in this order everywhere. A protected environment asks before every change. Each one has its own
                        encryption key.
                    </p>
                </div>
                <div className="side">
                    {environments.length === 0 && (
                        <button
                            className="act"
                            onClick={() =>
                                api
                                    .createDefaults()
                                    .then(refresh)
                                    .catch((error: unknown) => say(messageOf(error)))
                            }
                        >
                            Create the usual five
                        </button>
                    )}
                    <button className="act primary" onClick={() => setAdding(!adding)}>
                        <Icon name={adding ? 'x' : 'plus'} size={15} />
                        {adding ? 'Close' : 'New environment'}
                    </button>
                </div>
            </div>
            {adding && (
                <EnvironmentForm
                    title="New environment"
                    initial={{ name: '', color: ENVIRONMENT_COLORS[3] as string, protected: false }}
                    onCancel={() => setAdding(false)}
                    onSave={(input) =>
                        api.createEnvironment(input).then(async ({ environment }) => {
                            setAdding(false);
                            await refresh();
                            say(`${environment.name} created, with its own key`);
                        })
                    }
                />
            )}
            <div className="panel">
                <table className="ptable">
                    <thead>
                        <tr>
                            <th>Environment</th>
                            <th>Address</th>
                            <th>Holds</th>
                            <th>Protection</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {environments.map((environment, index) =>
                            editing === environment.id ? (
                                <tr key={environment.id}>
                                    <td colSpan={5} style={{ padding: 0 }}>
                                        <EnvironmentForm
                                            title={`Edit ${environment.name}`}
                                            initial={{
                                                name: environment.name,
                                                slug: environment.slug,
                                                color: environment.color,
                                                protected: environment.protected,
                                            }}
                                            onCancel={() => setEditing(null)}
                                            onSave={(input) =>
                                                guarded(shell, environment, `edit ${environment.name}`, (confirm) =>
                                                    api.updateEnvironment(environment.id, input, confirm),
                                                ).then(async () => {
                                                    setEditing(null);
                                                    await refresh();
                                                })
                                            }
                                        />
                                    </td>
                                </tr>
                            ) : (
                                <tr key={environment.id}>
                                    <td>
                                        <div className="envrow-name">
                                            <i className="dot halo" style={{ '--c': environment.color } as CSSProperties} />
                                            <Link className="strong" to={vaultPath(environment.slug)}>
                                                {environment.name}
                                            </Link>
                                        </div>
                                    </td>
                                    <td className="mono small">/{environment.slug}</td>
                                    <td className="small">
                                        {plural(environment.sections.length, 'section')},{' '}
                                        {plural(
                                            environment.sections.reduce((sum, item) => sum + item.variableCount, 0),
                                            'variable',
                                        )}
                                    </td>
                                    <td>
                                        {environment.protected ? (
                                            <span className="chip warn">
                                                <Icon name="lock" size={12} /> asks before changes
                                            </span>
                                        ) : (
                                            <span className="small">none</span>
                                        )}
                                    </td>
                                    <td className="row-actions">
                                        <span className="order">
                                            <button className="iconbtn" title="Up" disabled={index === 0} onClick={() => move(index, -1)}>
                                                <Icon name="up" size={15} />
                                            </button>
                                            <button
                                                className="iconbtn"
                                                title="Down"
                                                disabled={index === environments.length - 1}
                                                onClick={() => move(index, 1)}
                                            >
                                                <Icon name="down" size={15} />
                                            </button>
                                        </span>
                                        <button className="act small" onClick={() => setEditing(environment.id)}>
                                            Edit
                                        </button>
                                        <button className="act small danger" onClick={() => setDeleting(environment)}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ),
                        )}
                        {environments.length === 0 && (
                            <tr>
                                <td colSpan={5} className="empty">
                                    No environments yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {deleting && (
                <DeleteEnvironment environment={deleting} onClose={() => setDeleting(null)} onDone={() => (setDeleting(null), void refresh())} />
            )}
        </main>
    );
}

function EnvironmentForm(props: {
    title: string;
    initial: EnvironmentInput;
    onCancel: () => void;
    onSave: (input: EnvironmentInput) => Promise<void>;
}): ReactNode {
    const [name, setName] = useState(props.initial.name);
    const [slug, setSlug] = useState(props.initial.slug ?? '');
    const [color, setColor] = useState(props.initial.color);
    const [isProtected, setProtected] = useState(props.initial.protected);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const address = slugify(slug || name);

    return (
        <form
            className="panel form-card"
            onSubmit={(event) => {
                event.preventDefault();
                setBusy(true);
                setError(null);
                props
                    .onSave({ name: name.trim(), slug: address, color, protected: isProtected })
                    .catch((reason: unknown) => !(reason instanceof Cancelled) && setError(messageOf(reason)))
                    .finally(() => setBusy(false));
            }}
        >
            <div className="panel-head">
                <span className="title">{props.title}</span>
                <span className="note">its variables live at /{address || '…'}</span>
            </div>
            <div className="form-grid">
                <label>
                    <span className="micro">Name</span>
                    <input
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        maxLength={NAME_MAX}
                        placeholder="Staging"
                        required
                        autoFocus
                    />
                </label>
                <label>
                    <span className="micro">Address · from the name unless set</span>
                    <input value={slug} onChange={(event) => setSlug(event.target.value)} placeholder={slugify(name) || 'staging'} maxLength={40} />
                </label>
                <label>
                    <span className="micro">Colour · the edge and the pill</span>
                    <span className="swatches">
                        {ENVIRONMENT_COLORS.map((swatch) => (
                            <button
                                key={swatch}
                                type="button"
                                className={`swatch ${swatch === color ? 'on' : ''}`}
                                style={{ '--c': swatch } as CSSProperties}
                                onClick={() => setColor(swatch)}
                                title={swatch}
                            />
                        ))}
                    </span>
                </label>
            </div>
            <div style={{ padding: '0 18px 14px' }}>
                <button type="button" className={`toggle ${isProtected ? 'on' : ''}`} onClick={() => setProtected(!isProtected)}>
                    <span className="sw" />
                    Protected: every change asks for a confirmation (production)
                </button>
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
                <button type="button" className="act" onClick={props.onCancel}>
                    Cancel
                </button>
                <button className="act primary" disabled={busy || !name.trim()}>
                    {busy ? 'Saving…' : 'Save'}
                </button>
            </div>
        </form>
    );
}

function DeleteEnvironment(props: { environment: Environment; onClose: () => void; onDone: () => void }): ReactNode {
    useOverlay();
    const { say } = useShell();
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const variables = props.environment.sections.reduce((sum, section) => sum + section.variableCount, 0);

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <form
                className="dialog float protected"
                style={{ '--c': props.environment.color } as CSSProperties}
                onKeyDown={(event) => event.key === 'Escape' && props.onClose()}
                onSubmit={(event) => {
                    event.preventDefault();
                    setBusy(true);
                    api.deleteEnvironment(props.environment.id, typed)
                        .then(() => {
                            say(`${props.environment.name} deleted`);
                            props.onDone();
                        })
                        .catch((error: unknown) => say(messageOf(error)))
                        .finally(() => setBusy(false));
                }}
            >
                <div className="sheet-head">
                    <div>
                        <h2>Delete {props.environment.name}?</h2>
                        <p>
                            {plural(props.environment.sections.length, 'section')} and {plural(variables, 'variable')} go with it, history included,
                            for good.
                        </p>
                    </div>
                    <button type="button" className="closebtn" onClick={props.onClose}>
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div className="dialog-body">
                    <p>
                        Type <b>{props.environment.slug}</b> to confirm.
                    </p>
                    <input
                        value={typed}
                        onChange={(event) => setTyped(event.target.value)}
                        autoFocus
                        spellCheck={false}
                        placeholder={props.environment.slug}
                    />
                    <div className="dialog-actions">
                        <button type="button" className="act" onClick={props.onClose}>
                            Cancel
                        </button>
                        <button className="act danger solid" disabled={busy || typed !== props.environment.slug}>
                            {busy ? 'Deleting…' : `Delete ${props.environment.name}`}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
